// Incident quick actions shared by the alert drawer, the incidents pages and
// the ops view (PRD §5.8): ACK (with optional note), Mute (duration and reason
// are mandatory), Trace path (ad-hoc traceroute from a probe).
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader } from "lucide-react";
import { toast } from "sonner";

import {
  getGetIncidentQueryKey, getListIncidentsQueryKey, getListMonitorStatesQueryKey, getListMutesQueryKey,
  useAckIncident, useCreateAdhocRequest, useGetAdhocRequest, useMuteMonitor,
  type AdhocKind, type AdhocRequest, type Incident, type Probe,
} from "@/api/generated";
import { ErrorNote, Field, errorText } from "@/components/common/common";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TraceView, type Trace } from "@/components/paths/TraceView";
import { ms, pct } from "@/lib/format";

export function AckDialog({ incident, open, onOpenChange }: { incident: Incident | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [note, setNote] = useState("");
  const ack = useAckIncident();
  useEffect(() => {
    if (open) {
      setNote("");
      ack.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!incident) return null;
  const submit = () =>
    ack.mutate(
      { id: incident.id, data: note.trim() ? { note: note.trim() } : {} },
      {
        onSuccess: () => {
          toast.success("Acknowledged", { description: incident.title });
          qc.invalidateQueries({ queryKey: getListIncidentsQueryKey() });
          qc.invalidateQueries({ queryKey: getGetIncidentQueryKey(incident.id) });
          onOpenChange(false);
        },
      },
    );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Acknowledge incident</DialogTitle>
          <DialogDescription>{incident.title}. Acknowledging silences the audible alert and stops escalation to the next tier.</DialogDescription>
        </DialogHeader>
        <Field label="Note (optional)" hint="Starts the notes thread, e.g. what you are checking.">
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={4000} autoFocus />
        </Field>
        <ErrorNote error={ack.error} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={ack.isPending}>
            {ack.isPending && <Loader className="size-3.5 animate-spin" />} Acknowledge
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const MUTE_PRESETS = [
  { label: "15 minutes", minutes: 15 },
  { label: "30 minutes", minutes: 30 },
  { label: "1 hour", minutes: 60 },
  { label: "2 hours", minutes: 120 },
  { label: "4 hours", minutes: 240 },
  { label: "8 hours", minutes: 480 },
  { label: "24 hours", minutes: 1440 },
];

export function MuteDialog({ monitorId, monitorName, open, onOpenChange }: { monitorId: string | null; monitorName?: string; open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [minutes, setMinutes] = useState("60");
  const [reason, setReason] = useState("");
  const mute = useMuteMonitor();
  useEffect(() => {
    if (open) {
      setReason("");
      setMinutes("60");
      mute.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  if (!monitorId) return null;
  const submit = () =>
    mute.mutate(
      { id: monitorId, data: { duration_minutes: Number(minutes), reason: reason.trim() } },
      {
        onSuccess: () => {
          toast.success("Notifications muted", { description: monitorName });
          qc.invalidateQueries({ queryKey: getListMutesQueryKey() });
          qc.invalidateQueries({ queryKey: getListMonitorStatesQueryKey() });
          onOpenChange(false);
        },
      },
    );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Mute notifications</DialogTitle>
          <DialogDescription>
            {monitorName ?? "This monitor"} keeps being checked and still shows its real state; only notifications are silenced.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <Field label="Duration">
            <Select value={minutes} onValueChange={setMinutes}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MUTE_PRESETS.map((p) => <SelectItem key={p.minutes} value={String(p.minutes)}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reason" hint="Required. Shown to everyone on the NOC.">
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} autoFocus />
          </Field>
        </div>
        <ErrorNote error={mute.error} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={mute.isPending || reason.trim() === ""}>
            {mute.isPending && <Loader className="size-3.5 animate-spin" />} Mute
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Polls an ad-hoc request until it finishes (the stream also pushes the result). */
export function useAdhocResult(id: string | null) {
  return useGetAdhocRequest(id ?? "", {
    query: {
      enabled: id !== null,
      refetchInterval: (q) => {
        const s = q.state.data?.status;
        return s === "PENDING" || s === "SENT" || s === undefined ? 2000 : false;
      },
    },
  });
}

export function AdhocResultView({ req }: { req: AdhocRequest }) {
  if (req.status === "PENDING" || req.status === "SENT") {
    return (
      <p className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader className="size-4 animate-spin" /> Waiting for the probe… {req.kind === "TRACEROUTE" && "(a traceroute can take up to a minute)"}
      </p>
    );
  }
  if (req.status === "FAILED" || req.status === "EXPIRED") {
    return <ErrorNote error={new Error(req.error || (req.status === "EXPIRED" ? "The probe did not answer in time." : "The test failed."))} />;
  }
  const r = (req.result ?? {}) as Record<string, unknown>;
  if (Array.isArray(r.hops)) return <TraceView trace={r as unknown as Trace} />;
  const timings = r.timings as Record<string, number> | undefined;
  const rows: Array<[string, string]> = [
    ["Result", r.success ? "Success" : "Failure"],
    ...(r.sent !== undefined && req.kind === "ICMP" ? [["Received", `${r.received}/${r.sent}`] as [string, string]] : []),
    ...(r.rtt_avg_ms !== undefined ? [["RTT min/avg/max", `${ms(r.rtt_min_ms as number)} / ${ms(r.rtt_avg_ms as number)} / ${ms(r.rtt_max_ms as number)}`] as [string, string]] : []),
    ...(r.jitter_ms !== undefined ? [["Jitter", ms(r.jitter_ms as number)] as [string, string]] : []),
    ...(r.loss_pct !== undefined ? [["Loss", pct(r.loss_pct as number)] as [string, string]] : []),
    ...(r.http_status !== undefined ? [["HTTP status", String(r.http_status)] as [string, string]] : []),
    ...(timings ? [["DNS / connect / TLS / TTFB", `${ms(timings.dns_ms)} / ${ms(timings.connect_ms)} / ${ms(timings.tls_ms)} / ${ms(timings.ttfb_ms)}`] as [string, string]] : []),
    ...(r.cert_days_remaining !== undefined ? [["Certificate expires in", `${r.cert_days_remaining} days`] as [string, string]] : []),
    ...(r.error ? [["Error", String(r.error)] as [string, string]] : []),
  ];
  return (
    <dl className="tabular grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 rounded-md border p-3 text-sm">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="text-muted-foreground">{k}</dt>
          <dd className="break-all">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

const DEFAULT_PORTS: Record<AdhocKind, string> = { ICMP: "", TCP: "443", HTTP: "", TRACEROUTE: "" };

/** Run an ad-hoc test from one probe and show the result live. */
export function AdhocDialog({
  open, onOpenChange, probes, initialProbeId, initialTarget = "", initialKind = "TRACEROUTE", monitorId,
}: {
  open: boolean; onOpenChange: (v: boolean) => void; probes: Probe[]; initialProbeId?: string; initialTarget?: string;
  initialKind?: AdhocKind; monitorId?: string;
}) {
  const online = probes.filter((p) => p.lifecycle === "ACTIVE");
  const [probeId, setProbeId] = useState("");
  const [kind, setKind] = useState<AdhocKind>(initialKind);
  const [target, setTarget] = useState(initialTarget);
  const [port, setPort] = useState("");
  const [reqId, setReqId] = useState<string | null>(null);
  const create = useCreateAdhocRequest();
  const result = useAdhocResult(reqId);

  useEffect(() => {
    if (!open) return;
    setProbeId(initialProbeId ?? online.find((p) => p.state.status === "ONLINE")?.id ?? "");
    setKind(initialKind);
    setTarget(initialTarget);
    setPort(DEFAULT_PORTS[initialKind]);
    setReqId(null);
    create.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const run = () => {
    setReqId(null);
    create.mutate(
      {
        id: probeId,
        data: { kind, target: target.trim(), ...(kind === "TCP" ? { port: Number(port) } : {}), ...(monitorId ? { monitor_id: monitorId } : {}) },
      },
      { onSuccess: (r) => setReqId(r.id), onError: (e) => toast.error("Could not start the test", { description: errorText(e) }) },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Run a test from a probe</DialogTitle>
          <DialogDescription>Runs once, right now, from the probe you pick. Traceroute shows the path hop by hop.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-[1fr_9rem]">
          <Field label="Probe">
            <Select value={probeId} onValueChange={setProbeId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="Choose a probe" /></SelectTrigger>
              <SelectContent>
                {online.map((p) => (
                  <SelectItem key={p.id} value={p.id} disabled={p.state.status !== "ONLINE"}>
                    {p.name} {p.state.status !== "ONLINE" && "(offline)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Test">
            <Select value={kind} onValueChange={(v) => { setKind(v as AdhocKind); setPort(DEFAULT_PORTS[v as AdhocKind]); }}>
              <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(["TRACEROUTE", "ICMP", "TCP", "HTTP"] as AdhocKind[]).map((k) => <SelectItem key={k} value={k}>{k === "TRACEROUTE" ? "Traceroute" : k}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label={kind === "HTTP" ? "URL" : "Target host or IP"} className={kind === "TCP" ? "" : "sm:col-span-2"}>
            <Input value={target} onChange={(e) => setTarget(e.target.value)} placeholder={kind === "HTTP" ? "https://example.com/health" : "203.0.113.10"} />
          </Field>
          {kind === "TCP" && (
            <Field label="Port">
              <Input value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ""))} inputMode="numeric" />
            </Field>
          )}
        </div>
        {reqId && result.data && <AdhocResultView req={result.data} />}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button onClick={run} disabled={!probeId || !target.trim() || create.isPending || (kind === "TCP" && !port)}>
            {create.isPending && <Loader className="size-3.5 animate-spin" />} Run test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One set of action dialogs for a whole list (drawer, tables). */
export function useIncidentActions(probes: Probe[]) {
  const [ackFor, setAckFor] = useState<Incident | null>(null);
  const [muteFor, setMuteFor] = useState<{ id: string; name?: string } | null>(null);
  const [traceFor, setTraceFor] = useState<{ target: string; probeId?: string; monitorId?: string; kind?: AdhocKind } | null>(null);
  const dialogs = (
    <>
      <AckDialog incident={ackFor} open={ackFor !== null} onOpenChange={(v) => !v && setAckFor(null)} />
      <MuteDialog monitorId={muteFor?.id ?? null} monitorName={muteFor?.name} open={muteFor !== null} onOpenChange={(v) => !v && setMuteFor(null)} />
      <AdhocDialog
        open={traceFor !== null}
        onOpenChange={(v) => !v && setTraceFor(null)}
        probes={probes}
        initialProbeId={traceFor?.probeId}
        initialTarget={traceFor?.target}
        initialKind={traceFor?.kind ?? "TRACEROUTE"}
        monitorId={traceFor?.monitorId}
      />
    </>
  );
  return { ack: setAckFor, mute: setMuteFor, trace: setTraceFor, dialogs };
}
